import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import PageBreadcrumb from "@/components/base/PageBreadcrumb";
import { teamMembers } from "@/mocks/site";
import { videos } from "@/mocks/videos";
import { articles } from "@/mocks/articles";
import { posts } from "@/mocks/posts";
import usePageMeta from "@/hooks/usePageMeta";

const values = [
  {
    icon: "ri-heart-3-line",
    title: "轻松优先",
    text: "所有内容都按「看完会不会更松弛」这个标准挑选，不追求刺激，也不制造焦虑。",
  },
  {
    icon: "ri-shield-check-line",
    title: "边界清晰",
    text: "尊重每个人的感受和节奏。任何互动体验，都以双方自愿、随时可以停止为前提。",
  },
  {
    icon: "ri-lightbulb-line",
    title: "认真有趣",
    text: "趣味不等于敷衍。科普会查资料，排版会校对，连标点都希望读起来舒服。",
  },
];

export default function AboutPage() {
  usePageMeta(
    "关于我们 - 挠脚心网站",
    "了解挠脚心网站的定位、团队与联系方式。我们是一个以轻松趣味内容为核心的小站，所有内容均为虚构演示。",
    "关于我们,挠脚心网站,团队介绍,联系方式"
  );

  const stats = [
    { value: String(videos.length), label: "演示视频" },
    { value: String(articles.length), label: "趣味文章" },
    { value: String(posts.length), label: "社区话题" },
    { value: "2026", label: "站点起始年份" },
  ];

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <div className="w-full border-b border-background-200 bg-background-100/70">
          <div className="mx-auto w-full max-w-[1240px] px-4 py-8 md:px-6 md:py-10">
            <PageBreadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
            <div className="mt-4 grid grid-cols-1 items-center gap-6 lg:grid-cols-12">
              <div className="lg:col-span-7">
                <p className="mb-2 flex items-center gap-2 text-[11px] tracking-[0.24em] text-accent-600 uppercase">
                  <span className="h-1.5 w-1.5 rounded-full bg-accent-500"></span>
                  About Us
                </p>
                <h1 className="font-heading text-2xl leading-snug text-foreground-950 md:text-4xl">
                  一个关于轻松、
                  <br className="hidden sm:block" />
                  好奇和一点点笑声的小站
                </h1>
                <p className="mt-4 max-w-xl text-sm leading-relaxed text-foreground-700 md:text-base">
                  挠脚心网站起步于一个很简单的想法：让人在十分钟里真正放松一次。我们把挠痒相关的短片、科普和生活提案放在一起，
                  又留了一块地方给愿意慢慢聊的人。这里没有复杂的规则，也不需要登录。
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <a
                    href="mailto:hello@ticklefeet.demo"
                    className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-foreground-950 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-mail-line text-base"></i>
                    联系我们
                  </a>
                  <a
                    href="#team"
                    className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-50"
                  >
                    认识团队
                    <i className="ri-arrow-down-line text-base"></i>
                  </a>
                </div>
              </div>
              <div className="lg:col-span-5">
                <div className="relative h-[240px] w-full overflow-hidden rounded-lg bg-background-200 md:h-[300px]">
                  <img
                    src="https://readdy.ai/api/search-image?query=Warm%20cozy%20illustration%20of%20a%20small%20sunlit%20studio%20desk%20with%20a%20notebook%2C%20a%20feather%2C%20a%20cup%20of%20tea%20and%20a%20potted%20plant%2C%20soft%20cream%20walls%2C%20golden%20afternoon%20light%2C%20minimal%20flat%20editorial%20style%20with%20honey%20tones&width=800&height=600&seq=about-hero-01&orientation=landscape"
                    alt="挠脚心网站的小工作室氛围插图"
                    title="挠脚心网站 - 团队工作日常"
                    className="h-full w-full object-cover object-top"
                  />
                  <div className="absolute bottom-3 left-3 right-3 rounded-md bg-background-50/90 px-3 py-2 text-[11px] text-foreground-700">
                    我们的小工作室 · 插图为虚构演示画面
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mx-auto w-full max-w-[1240px] px-4 py-10 md:px-6 md:py-14">
          <section className="grid grid-cols-1 gap-6 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <h2 className="flex items-center gap-2 font-heading text-xl text-foreground-950 md:text-2xl">
                <span className="h-4 w-1 rounded-full bg-primary-500"></span>
                网站简介
              </h2>
              <div className="mt-4 flex flex-col gap-4 text-sm leading-loose text-foreground-700 md:text-base">
                <p>
                  我们不做大而全的门户，只做一件事：把「挠痒」这个有点好笑的趣味主题，整理成看得下去、读得进去的内容。
                  视频板块收录了轻挠、搞笑和互动三类短片，文章板块负责把常见的问题讲清楚，社区则留给大家交换真实体验。
                </p>
                <p>
                  内容更新节奏是每周一次。视频以封面预览形式展示，文章为完整的图文排版，社区帖子为示范数据。你看到的所有标题、作者和描述都是虚构的，
                  用来展示这个网站完整的样子，不涉及任何真实人物。
                </p>
                <p>
                  我们更在意氛围，而不是流量。这里的讨论默认是温和的：不评判别人的感受，不催促任何人尝试，也不把「怕痒」当成缺点。
                  如果你只是想安静地看一段视频，这里同样欢迎。
                </p>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {stats.map((item) => (
                  <div
                    key={item.label}
                    className="rounded-lg border border-background-200 bg-background-50 px-3 py-4 text-center"
                  >
                    <p className="font-heading text-xl text-foreground-950 md:text-2xl">{item.value}</p>
                    <p className="mt-1 text-[11px] text-foreground-500">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-lg border border-primary-200 bg-primary-50 p-5 md:p-6">
                <h2 className="flex items-center gap-2 font-heading text-base text-foreground-950">
                  <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-500 text-foreground-950">
                    <i className="ri-mail-send-line text-base"></i>
                  </span>
                  联系方式
                </h2>
                <div className="mt-4 flex flex-col gap-3 text-sm">
                  <a
                    href="mailto:hello@ticklefeet.demo"
                    className="flex items-center gap-2 text-foreground-800 transition-colors hover:text-accent-700"
                  >
                    <i className="ri-mail-line text-base text-foreground-500"></i>
                    hello@ticklefeet.demo
                  </a>
                  <p className="flex items-center gap-2 text-foreground-700">
                    <i className="ri-time-line text-base text-foreground-500"></i>
                    工作日 10:00 - 18:00 回复
                  </p>
                  <p className="flex items-center gap-2 text-foreground-700">
                    <i className="ri-map-pin-line text-base text-foreground-500"></i>
                    线上运营，无实体门店
                  </p>
                </div>
                <p className="mt-4 border-t border-primary-200 pt-3 text-xs leading-relaxed text-foreground-600">
                  邮箱为演示地址。内容合作、内容纠错或社区问题，都可以从这里找到我们。
                </p>
              </div>

              <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                <h2 className="flex items-center gap-2 font-heading text-base text-foreground-950">
                  <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                    <i className="ri-file-text-line text-base"></i>
                  </span>
                  内容说明
                </h2>
                <ul className="mt-4 flex flex-col gap-3 text-xs leading-relaxed text-foreground-600">
                  <li className="flex gap-2">
                    <i className="ri-checkbox-circle-line mt-0.5 text-base text-secondary-500"></i>
                    所有视频仅提供封面预览，不提供真实播放与下载。
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-checkbox-circle-line mt-0.5 text-base text-secondary-500"></i>
                    收藏记录保存在本机浏览器，换设备不会同步。
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-checkbox-circle-line mt-0.5 text-base text-secondary-500"></i>
                    社区暂未开放登录，发帖与回复按钮为演示提示。
                  </li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mt-12 md:mt-16">
            <h2 className="font-heading text-xl text-foreground-950 md:text-2xl">我们在意的事</h2>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
              三条简单的原则，决定了我们怎么选内容、怎么写文章、怎么管理社区。
            </p>
            <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
              {values.map((item, index) => (
                <div
                  key={item.title}
                  className={`rounded-lg border p-5 ${
                    index === 1
                      ? "border-accent-200 bg-accent-50"
                      : "border-background-200 bg-background-50"
                  }`}
                >
                  <span
                    className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                      index === 1 ? "bg-accent-500 text-background-50" : "bg-primary-100 text-primary-800"
                    }`}
                  >
                    <i className={`${item.icon} text-lg`}></i>
                  </span>
                  <h3 className="mt-4 font-heading text-base text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
                </div>
              ))}
            </div>
          </section>

          <section id="team" className="mt-12 scroll-mt-24 md:mt-16">
            <h2 className="font-heading text-xl text-foreground-950 md:text-2xl">团队介绍</h2>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
              四个人的小团队，分工很简单：有人负责内容，有人负责画面，有人负责社区里的每一句回复。
            </p>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {teamMembers.map((member) => (
                <div
                  key={member.name}
                  className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-4"
                >
                  <div className="h-40 w-full overflow-hidden rounded-md bg-background-200">
                    <img
                      src={member.avatar}
                      alt={`${member.name}的插画头像`}
                      title={`${member.name} - ${member.role}`}
                      className="h-full w-full object-cover object-top"
                    />
                  </div>
                  <div className="mt-3">
                    <p className="font-heading text-base text-foreground-950">{member.name}</p>
                    <p className="mt-0.5 text-[11px] text-accent-700">{member.role}</p>
                  </div>
                  <p className="mt-2 text-xs leading-relaxed text-foreground-600">{member.bio}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-12 rounded-lg bg-secondary-100 p-5 md:mt-16 md:p-8">
            <h2 className="flex items-center gap-2 font-heading text-lg text-foreground-950">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-500 text-background-50">
                <i className="ri-error-warning-line text-base"></i>
              </span>
              版权与免责声明
            </h2>
            <div className="mt-4 flex flex-col gap-3 text-xs leading-relaxed text-foreground-700 md:text-sm">
              <p>
                本站为演示性质的趣味网站，站内所有视频、文章、帖子、作者名称、团队信息与联系方式均为虚构内容，不对应任何真实人物或机构。
                视频不提供真实播放资源，社区不提供真实登录与发帖功能。
              </p>
              <p>
                站内内容仅用于轻松娱乐与页面展示，不构成医疗、心理、法律或任何专业建议。任何互动体验请在双方明确同意、随时可停止的前提下进行；
                如感到不适，请立即停止并寻求合适的帮助。
              </p>
              <p>© 2026 挠脚心网站 · 本站保留对演示内容的最终解释权。</p>
            </div>
          </section>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}